import { NodeType, randomDhtAddress, toDhtAddressRaw, } from '@streamr/dht';
import { createNetworkNode, } from '@streamr/trackerless-network';
import { ContentType, EncryptionType, SignatureType, } from '@streamr/trackerless-network';
import { MetricsContext, StreamPartIDUtils, binaryToHex, hexToBinary, toStreamID, } from '@streamr/utils';
import EventEmitter from 'eventemitter3';
import { v4 as uuidv4 } from 'uuid';
import { Message } from '../../proto/messages';
import { KeyStore } from '../helpers/KeyStore';
import { MessageDecrypterWrapper } from './MessageDecrypterWrapper';
import { MessageEncrypterWrapper } from './MessageEncrypterWrapper';
import { MessageSignerWrapper } from './MessageSignerWrapper';
import { MessageValidatorWrapper } from './MessageValidatorWrapper';
const globalEntryPoints = [
    {
        nodeId: hexToBinary('6f5b53812fd9cc07f225a0b3a6aa5b96672e852e'),
        websocket: {
            host: 'polygon-entrypoint-4.streamr.network',
            port: 443,
            tls: true,
        },
        type: NodeType.NODEJS,
    },
    {
        nodeId: hexToBinary('16dcfb6078e54567435c6bee8907f5e4f5d65a1b'),
        type: NodeType.NODEJS,
        websocket: {
            host: 'streamrtv-entrypoint-1.streamr.network',
            port: 443,
            tls: true,
        },
    },
    {
        nodeId: hexToBinary('d32a023fbb60efd874211d9d5718ddbfa1e3cb1c'),
        type: NodeType.NODEJS,
        websocket: {
            host: 'streamrtv-entrypoint-2.streamr.network',
            port: 443,
            tls: true,
        },
    },
];
// TODO: fix this to work the same way as in the normal Streamr network
let sequenceNumber = 0;
const PLUM_TREE_OPTIMISATION_ENABLED = true;
export class StreamrWrapper extends EventEmitter {
    networkNode;
    contentStreamPartId;
    controlStreamPartId;
    aesKey;
    publisherPublicKey;
    messageDecrypter;
    messageValidator;
    messageEncrypter;
    messageSigner;
    localNodeId = randomDhtAddress();
    localPeerDescriptor = {
        nodeId: toDhtAddressRaw(this.localNodeId),
        type: NodeType.NODEJS,
    };
    metricsContext;
    uploadRateBytesPerSecond = 0;
    downloadRateBytesPerSecond = 0;
    abortController = new AbortController();
    constructor(aesKey) {
        super();
        this.aesKey = hexToBinary(aesKey);
        this.metricsContext = new MetricsContext();
        this.networkNode = createNetworkNode({
            layer0: {
                peerDescriptor: this.localPeerDescriptor,
                entryPoints: globalEntryPoints,
                webrtcAllowPrivateAddresses: true,
                iceServers: [
                    { url: 'stun:stun.streamr.network', port: 5349 },
                    {
                        url: 'turn:turn.streamr.network',
                        port: 5349,
                        username: 'BrubeckTurn1',
                        password: 'MIlbgtMw4nhpmbgqRrht1Q==',
                    },
                    {
                        url: 'turn:turn.streamr.network',
                        port: 5349,
                        username: 'BrubeckTurn1',
                        password: 'MIlbgtMw4nhpmbgqRrht1Q==',
                        tcp: true,
                    },
                    {
                        url: 'turn:turn-us.streamr.network',
                        port: 5349,
                        username: 'BrubeckTurn1',
                        password: 'MIlbgtMw4nhpmbgqRrht1Q==',
                    },
                ],
            },
            networkNode: {
                streamPartitionMinPropagationTargets: 0,
                streamPartitionMaxPropagationBufferSize: 0,
                doNotBufferWhileConnecting: true,
            },
            metricsContext: this.metricsContext,
        });
        this.metricsContext.createReportProducer((report) => {
            this.uploadRateBytesPerSecond = Math.round(report.node.sendBytesPerSecond ?? 0);
            this.downloadRateBytesPerSecond = Math.round(report.node.receiveBytesPerSecond ?? 0);
        }, 10 * 1000, this.abortController.signal);
    }
    async start(streamPartIdString) {
        console.log('StreamrWrapper::start()', streamPartIdString);
        this.messageDecrypter = new MessageDecrypterWrapper();
        this.messageValidator = new MessageValidatorWrapper();
        this.messageEncrypter = new MessageEncrypterWrapper();
        this.messageSigner = new MessageSignerWrapper();
        await this.messageEncrypter.init(binaryToHex(this.aesKey));
        await this.messageValidator.init();
        await this.messageDecrypter.init(binaryToHex(this.aesKey));
        await this.messageSigner.init();
        await this.networkNode.start();
        this.contentStreamPartId = StreamPartIDUtils.parse(streamPartIdString);
        this.controlStreamPartId = StreamPartIDUtils.parse(`${toStreamID(this.contentStreamPartId)}#1`);
        await this.networkNode.join(this.contentStreamPartId, undefined, {
            plumTreeOptimisation: PLUM_TREE_OPTIMISATION_ENABLED,
        });
        await this.networkNode.join(this.controlStreamPartId, undefined, {
            plumTreeOptimisation: false,
        });
        const ownPublicKey = KeyStore.getInstance().getPublicKey(StreamPartIDUtils.getStreamID(this.contentStreamPartId));
        this.messageEncrypter.on('encryptedData', async (encryptedMessage, messageId) => {
            const keyStore = KeyStore.getInstance();
            const streamId = StreamPartIDUtils.getStreamID(StreamPartIDUtils.parse(streamPartIdString));
            const privateKey = keyStore.getPrivateKey(streamId);
            this.messageSigner?.sign(messageId, encryptedMessage, hexToBinary(privateKey));
        });
        this.messageSigner.on('signedData', async (signature, messageId, content) => {
            const streamMessage = {
                messageId,
                body: {
                    oneofKind: 'contentMessage',
                    contentMessage: {
                        content,
                        contentType: ContentType.BINARY,
                        encryptionType: EncryptionType.AES,
                    },
                },
                signatureType: SignatureType.ECDSA_SECP256R1,
                signature,
            };
            await this.networkNode.broadcast(streamMessage);
        });
        this.messageDecrypter.on('decryptedData', (decryptedMessage, streamMessage) => {
            const message = Message.fromBinary(decryptedMessage);
            if (message.body.oneofKind === 'heartbeat') {
                this.emit('heartbeat', message.body.heartbeat);
            }
            else if (message.body.oneofKind === 'leave') {
                this.emit('leave', message.body.leave);
            }
            else if (message.body.oneofKind === 'mediaPacketFragment') {
                if (this.publisherPublicKey) {
                    this.messageValidator?.validate(this.publisherPublicKey, message, streamMessage);
                }
            }
            else if (message.body.oneofKind === 'viewerInfo') {
                if (this.publisherPublicKey) {
                    this.messageValidator?.validate(this.publisherPublicKey, message, streamMessage);
                }
            }
            else {
                console.error('Unknown message type', message.body.oneofKind);
            }
        });
        this.messageValidator.on('validatedData', (contentMessage) => {
            if (contentMessage.body.oneofKind === 'mediaPacketFragment') {
                this.emit('mediaPacketFragment', contentMessage.body.mediaPacketFragment);
            }
            else if (contentMessage.body.oneofKind === 'viewerInfo') {
                this.emit('viewerInfo', contentMessage.body.viewerInfo);
            }
            else {
                console.error('Unknown validated message type', contentMessage.body.oneofKind);
            }
        });
        this.networkNode.addMessageListener((streamMessage) => {
            // Don't process messages if published by this node
            if (binaryToHex(streamMessage.messageId?.publisherId ?? new Uint8Array()) !== ownPublicKey) {
                this.messageDecrypter?.decrypt(streamMessage);
            }
        });
    }
    startCreateStreamMessagePipeline(streamPartId, publisherId, message) {
        const streamId = StreamPartIDUtils.getStreamID(streamPartId);
        const streamPartition = StreamPartIDUtils.getStreamPartition(streamPartId);
        const messageId = {
            streamId: StreamPartIDUtils.getStreamID(streamPartId),
            streamPartition: StreamPartIDUtils.getStreamPartition(streamPartId),
            timestamp: Date.now(),
            sequenceNumber: sequenceNumber++,
            publisherId: publisherId,
            messageChainId: `${streamId}-${streamPartition}-${binaryToHex(publisherId)}`,
        };
        this.messageEncrypter?.encrypt(Message.toBinary(message), messageId);
    }
    setPublisherPublicKey(publicKey) {
        this.publisherPublicKey = publicKey;
    }
    async stop() {
        await this.networkNode.stop();
        this.abortController.abort();
    }
    getNodeId() {
        return this.localNodeId;
    }
    getDownloadRate() {
        return this.downloadRateBytesPerSecond;
    }
    getUploadRate() {
        return this.uploadRateBytesPerSecond;
    }
    getNeighbors(streamPartId) {
        return this.networkNode.getNeighbors(streamPartId);
    }
    async startPublishMessagePipeline(message, streamPartId) {
        if (this.contentStreamPartId === undefined) {
            throw new Error('Stream part ID is not set');
        }
        const keyStore = KeyStore.getInstance();
        const streamId = StreamPartIDUtils.getStreamID(streamPartId);
        const publisherId = keyStore.getPublicKey(streamId);
        this.startCreateStreamMessagePipeline(this.contentStreamPartId, hexToBinary(publisherId), message);
    }
    async publishHeartbeat(heartbeat) {
        const message = Message.create({
            messageId: uuidv4(),
            body: {
                oneofKind: 'heartbeat',
                heartbeat: heartbeat,
            },
        });
        if (this.controlStreamPartId !== undefined) {
            await this.startPublishMessagePipeline(message, this.controlStreamPartId);
        }
    }
    async publishLeave(leave) {
        const message = Message.create({
            messageId: uuidv4(),
            body: {
                oneofKind: 'leave',
                leave: leave,
            },
        });
        if (this.controlStreamPartId !== undefined) {
            await this.startPublishMessagePipeline(message, this.controlStreamPartId);
        }
    }
    async publishViewerInfo(viewerInfo) {
        const message = Message.create({
            messageId: uuidv4(),
            body: {
                oneofKind: 'viewerInfo',
                viewerInfo: viewerInfo,
            },
        });
        if (this.controlStreamPartId !== undefined) {
            await this.startPublishMessagePipeline(message, this.controlStreamPartId);
        }
    }
    async publishMediaPacketFragment(mediaPacketFragment) {
        const message = Message.create({
            messageId: uuidv4(),
            body: {
                oneofKind: 'mediaPacketFragment',
                mediaPacketFragment: mediaPacketFragment,
            },
        });
        if (this.contentStreamPartId !== undefined) {
            await this.startPublishMessagePipeline(message, this.contentStreamPartId);
        }
    }
}
